import * as THREE from 'three'

import MTLLoader from './MTLLoader';
import OBJLoader from './OBJLoader';
import OrbitControls from './OrbitControls';

export default class ThreeElement {

    constructor(params = {}) {

        const {width, height} = params;

        this._x = undefined;
        this._y = undefined;
        this._z = undefined;
        this._rotationObject = 'group';

        this.renderer = ThreeElement.getRenderer(width, height);

        this.loadingManager = new THREE.LoadingManager();
        this.loadingManager.onLoad = () => this.animate();
        this.loadingManager.onProgress = (url, itemsLoaded, itemsTotal) => {
            this.animate();
            this.onProgess(itemsLoaded, itemsTotal);
        };
        this.loadingManager.onError = (url) => {
            console.log('There was an error loading ' + url);
        };
    }

    set xRotation(value) {
        this._x = value;
    }

    get xRotation() {
        return this._x;
    }

    set yRotation(value) {
        this._y = value;
    }

    get yRotation() {
        return this._y;
    }

    set zRotation(value) {
        this._z = value;
    }

    get zRotation() {
        return this._z;
    }

    get rotationObject() {
        return this._rotationObject;
    }

    set rotationObject(value) {
        this._rotationObject = value;
    }

    get domElement() {
        return this.renderer.domElement;
    }

    set onProgess(callback) {
        this.progressCallback = callback;
    }

    get onProgess() {
        return this.progressCallback && (typeof this.progressCallback === 'function') ?
                this.progressCallback :
                () => {};
    }

    setWidth(width, padding) {
        if(this.renderer) {
            const interalPadding = padding || 0;

            // Note, the parent div has an internal padding of 14,
            //  so in order to get the size right, we knock off twice the size of the padding
            this.renderer.setSize(width - interalPadding, width - interalPadding, true);
        }
    }

    dispose() {

        this.onProgess = null;
        this.xRotation = this.yRotation = this.zRotation = undefined;

        if(this.controls) {
            this.controls.dispose();
        }

        this.clearScene(this.scene);

        this.renderer.dispose();

        delete this.controls;
        delete this.loadingManager;
        delete this.renderer;
    }

    initialize() {

        this.camera = new THREE.PerspectiveCamera( 20, 1, 1, 2000 );
        this.camera.position.set( 0, 0, 7 );

        const ambientLight = new THREE.AmbientLight( 0xffffff, 1 );
        const leftLight = new THREE.PointLight( 0xFFFF00 );
        leftLight.position.set( 120, 0, 10 );

        const watermark = new THREE.TextureLoader().load( "images/watermark.png" );
        watermark.wrapS = THREE.RepeatWrapping;
        watermark.wrapT = THREE.RepeatWrapping;
        watermark.repeat.set( 5, 5 );

        this.scene = new THREE.Scene();
        this.scene.background = watermark;
        this.scene.add( leftLight );
        this.scene.add( ambientLight );
        this.scene.add( this.camera );

        this.controls = new OrbitControls( this.camera, this.renderer.domElement );
        this.controls.update();
    }

    animate() {
        requestAnimationFrame(() => this.animate());

        if(this.renderer) {
            this.renderer.render(this.scene, this.camera);
        }
    }

    setModel(model) {

        this.clearScene(this.scene);

        // If the modelId isn't set, then don't bother rendering or making calls
        if(!model) {
            return;
        }

        let mtlLoader = new MTLLoader(this.loadingManager);

        mtlLoader.setPath('https://s3.amazonaws.com/rift-assets/models/');
        mtlLoader.setCrossOrigin('*');
        mtlLoader.load(`${model}.mtl`, material => {
            material.preload();

            new OBJLoader(this.loadingManager)
                    .setMaterials( material )
                    .setPath( 'https://s3.amazonaws.com/rift-assets/models/' )
                    .load( `${model}.obj`, object => {

                        object.traverse( function( node ) {
                            if( node.material ) {
                                node.material.side = THREE.DoubleSide;
                            }
                        });

                        const centerObject = this.rotationObject === 'mesh' ? object.children[0] : object;

                        // Center the object in the middle of the world.
                        // Thank you StackOverflow!
                        //  https://stackoverflow.com/a/46165381/87761
                        new THREE.Box3().setFromObject( centerObject ).getCenter( centerObject.position ).multiplyScalar( -1 );

                        // Rotate the model for better viewing
                        if(this.xRotation) {
                            object.rotateX(this.xRotation);
                        }
                        if(this.yRotation) {
                            object.rotateY(this.yRotation);
                        }
                        if(this.zRotation) {
                            object.rotateZ(this.zRotation);
                        }

                        this.scene.add(object);

                        this.animate();

                    }, null, error => {console.error(error)});
        }, null, error => {
            console.error(error);
        });
    }

    clearScene(scene) {

        if(!scene) {
            return;
        }

        const objectsToRemove = this.cleanUpObjects(scene.children);

        for(let object of objectsToRemove) {
            scene.remove(object);
        }
    }

    cleanUpObjects(objects) {
        let removedObjects = [];

        for(let object of objects) {
            if (object.type === 'Mesh') {
                object.geometry.dispose();
                object.material.dispose();
                removedObjects.push(object);
            } else if(object.type === 'Group') {
                removedObjects.push(object);
                removedObjects.push(...this.cleanUpObjects(object.children));
            }
        }

        return removedObjects;
    }
};

ThreeElement.renderer = null;
ThreeElement.getRenderer = (width, height) => {

    if(ThreeElement.renderer) {
        return ThreeElement.renderer;
    }

    ThreeElement.renderer = new THREE.WebGLRenderer();
    ThreeElement.renderer.setPixelRatio( window.devicePixelRatio );
    ThreeElement.renderer.setSize( width || 400, height || 400 );
    ThreeElement.renderer.setClearAlpha(0);

    return ThreeElement.renderer;
}


// WEBPACK FOOTER //
// ./src/common/model-viewer/threejs/ThreeElement.js